import type { ProductCard } from "@island/catalog-core";

/**
 * Product cards inside a message.
 *
 * Rendered from the snapshot stored on the message, not from today's catalogue. That
 * is what makes a transcript answerable six months later: it shows what the customer
 * was actually offered, at the price they were actually quoted, even if the product
 * has since changed or gone.
 *
 * The same component serves the agent's own transcript and the customer's widget in
 * spirit — they are separate apps with separate stylesheets, but both read the same
 * field and neither resolves anything.
 */

export interface ProductCardsProps {
  readonly products: readonly ProductCard[];
}

function money(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    }).format(amount);
  } catch {
    // An unrecognised currency code should cost the symbol, not the number.
    return String(amount);
  }
}

export function ProductCards({ products }: ProductCardsProps) {
  if (products.length === 0) return null;

  return (
    <div className="product-cards">
      {products.map((product) => (
        <a
          className="product-card"
          key={product.productId}
          href={product.url}
          target="_blank"
          rel="noreferrer"
        >
          {product.imageUrl !== undefined && <img src={product.imageUrl} alt="" loading="lazy" />}

          <span className="detail">
            <strong>{product.name}</strong>

            {product.price !== null && (
              <span className="price">
                {/* "From" only when the product genuinely has a range — otherwise it
                    implies variants that do not exist. */}
                {product.priceIsFrom ? "From " : ""}
                {money(product.price.amount, product.price.currency)}
              </span>
            )}

            {product.availability === "out_of_stock" && (
              <span className="small error">Out of stock</span>
            )}
          </span>
        </a>
      ))}
    </div>
  );
}
